"use client";

import { useState } from "react";
import clsx from "clsx";
import { useTranslations } from "@/lib/compat/i18n";

/**
 * OrbIDCredential renders the unified credential card produced by Act 1.6.
 * Per Decision 20: an OrbID encapsulates five sub-credentials:
 *   1. LEI                       (GLEIF-issued)
 *   2. Corp DID                  (HCS-anchored Ed25519)
 *   3. Live Verification ref     (cryptographic proof of human attestation)
 *   4. KYB attestation refs      (entity-document hashes + AML)
 *   5. Verified Trader badge     (audience-facing summary)
 *
 * Click the card to expand — each sub-credential surfaces its own anchor.
 */
export type OrbIDCredentialProps = {
  orbid: string;
  lei: string;
  corpDid: string;
  liveVerificationRef?: string;
  kybAttestationRefs: string[];
  verifiedTraderTier: string;
  legalName: string;
  jurisdiction?: string;
  className?: string;
};

export function OrbIDCredential(props: OrbIDCredentialProps) {
  const t = useTranslations("orbIdCredential");
  const [expanded, setExpanded] = useState(false);
  return (
    <button
      type="button"
      onClick={() => setExpanded((v) => !v)}
      className={clsx(
        // OrbitraID emphasis treatment — 2 px solid border + double-glow
        // shadow stack to make the whole credential card the hero on 1.8.
        "relative w-full rounded-md border-2 border-state-verified bg-orbitra-ink/90 text-start text-orbitra-teal-soft backdrop-blur-md",
        "p-5 shadow-[0_0_60px_var(--state-verified-glow),0_0_18px_var(--state-verified)] transition-state",
        props.className,
      )}
    >
      <span
        aria-hidden="true"
        className="absolute -top-3 start-4 inline-flex items-center gap-1 rounded-full border border-state-verified bg-orbitra-ink px-3 py-0.5 font-display text-[10px] font-semibold uppercase tracking-[0.22em] text-state-verified shadow-[0_0_18px_var(--state-verified-glow)]"
      >
        Orbitra · ID
      </span>
      <div className="flex items-center justify-between">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-state-verified">{t("corporateOrbId")}</p>
          <p className="mt-1 font-display text-lg font-semibold text-orbitra-paper">{props.legalName}</p>
          <p className="mt-0.5 font-mono text-[11px] text-orbitra-muted">{props.orbid.replace(/^orbid:/, "corbid:")}{props.jurisdiction ? ` · ${props.jurisdiction}` : ""}</p>
        </div>
        <div className="flex flex-col items-end">
          <span className="rounded-full border border-state-verified/50 bg-state-verified/12 px-3 py-1 font-display text-[11px] font-semibold uppercase tracking-wider text-state-verified">
            {props.verifiedTraderTier} Trader
          </span>
        </div>
      </div>

      {expanded && (
        <div className="mt-5 grid grid-cols-1 gap-2 border-t border-state-verified/20 pt-4 text-[11px] sm:grid-cols-2">
          <SubCred  label={t("subCredLei")}              value={props.lei}                                          mono />
          <SubCred  label={t("subCredCorpDid")}         value={props.corpDid.replace(/orbid:/, "corbid:")}         mono />
          <SubCred  label={t("subCredLiveVerification")} value={props.liveVerificationRef ?? "—"}                   mono />
          <SubCred  label={t("subCredKybAttestations")} value={props.kybAttestationRefs.join("  ·  ")}             mono />
          <SubCred  label={t("subCredVerifiedTraderBadge")} value={`${props.verifiedTraderTier} · pillar: Verify`} />
        </div>
      )}

      <p className="mt-3 text-[10px] uppercase tracking-wider text-orbitra-muted">
        {expanded ? t("clickToCollapse") : t("clickToInspect")}
      </p>
    </button>
  );
}

function SubCred({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex flex-col">
      <span className="text-[9px] uppercase tracking-[0.18em] text-state-verified">{label}</span>
      <span className={clsx("mt-0.5 break-words text-orbitra-paper", mono && "font-mono")}>{value}</span>
    </div>
  );
}
